import { useCallback, useEffect, useState } from 'react';
import { YeuTo, YeuToInput, YeuToResponse } from '@/types/schemas/yeu-to.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseYeuToReturn {
  yeuTos: YeuTo[];
  isLoading: boolean;
  addYeuTo: (newFactor: YeuToInput) => Promise<void>;
  updateYeuTo: (uuid: string, updatedFactor: YeuToInput) => Promise<void>;
  deleteYeuTo: (uuid: string) => Promise<void>;
  refreshYeuTos: () => Promise<void>;
  getYeuToByCode: (code: string) => Promise<YeuTo | null>;
  getActiveYeuTos: () => Promise<YeuTo[]>;
  getYeuTosByType: (factorType: string) => Promise<YeuTo[]>;
}

/**
 * Hook for managing YeuTo (Factor) data
 *
 * This hook provides functions to fetch, create, update, and delete factors.
 */
export const useYeuTo = (initialYeuTos: YeuTo[] = []): UseYeuToReturn => {
  const [yeuTos, setYeuTos] = useState<YeuTo[]>(initialYeuTos);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchYeuTos = useCallback(async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<YeuToResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/`);
      setYeuTos(response.data.results);
    } catch (error) {
      console.error('Error fetching factors:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  const getYeuToByCode = async (code: string): Promise<YeuTo | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      // First check if we already have it in local state
      const found = yeuTos.find(item => item.ma_yt === code);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<YeuToResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/?ma_yt=${code}`);

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching factor by code:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveYeuTos = async (): Promise<YeuTo[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<YeuToResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/?status=1`);
      return response.data.results;
    } catch (error) {
      console.error('Error fetching active factors:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getYeuTosByType = async (factorType: string): Promise<YeuTo[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<YeuToResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/?loai_yt=${factorType}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching factors by type:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addYeuTo = async (newFactor: YeuToInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.post(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/`, newFactor);
      await fetchYeuTos();
    } catch (error) {
      console.error('Error adding factor:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateYeuTo = async (uuid: string, updatedFactor: YeuToInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.put(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/${uuid}/`, updatedFactor);
      await fetchYeuTos();
    } catch (error) {
      console.error('Error updating factor:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteYeuTo = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.YEU_TO}/${uuid}/`);
      setYeuTos(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting factor:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchYeuTos();
  }, [entity?.slug, fetchYeuTos]);

  return {
    yeuTos,
    isLoading,
    addYeuTo,
    updateYeuTo,
    deleteYeuTo,
    refreshYeuTos: fetchYeuTos,
    getYeuToByCode,
    getActiveYeuTos,
    getYeuTosByType
  };
};
